import axios from "./axiosInstance";
import { AxiosResponse } from "axios";
import config from "./config";
import { OnboardingStatus, OnboardingStatusValue } from "../types/onboarding";
import { safeGetItem } from "@mediatechgroup/app-version";

const BASE_URL = config.BASE_URL;

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

/**
 * Get tenant ID from URL path
 */
const getTenantId = (): string | null => {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  return pathParts[0] || null;
};

/**
 * Get authentication source (central or tenant)
 */
const getAuthSource = (): string | null => {
  return safeGetItem("authSource");
};

/**
 * Get authentication token
 */
const getAuthToken = (): string | null => {
  return safeGetItem("authToken");
};

/**
 * Get common headers for API requests
 */
const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const tenantId = getTenantId();
  if (tenantId) {
    headers["x-tenant-id"] = tenantId;
  }

  const token = getAuthToken();
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  const authSource = getAuthSource();
  if (authSource) {
    headers["x-auth-source"] = authSource;
  }

  return headers;
};

/**
 * Onboarding API client
 */
export const onboardingAPI = {
  /**
   * Get all onboarding status records for current user
   */
  getAll: async (): Promise<OnboardingStatus[]> => {
    try {
      const response: AxiosResponse<ApiResponse<OnboardingStatus[]>> =
        await axios.get(`${BASE_URL}/onboarding`, {
          headers: getHeaders(),
        });

      return response.data?.data || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get onboarding status for a specific page
   */
  getStatus: async (pageKey: string): Promise<OnboardingStatus | null> => {
    try {
      const response: AxiosResponse<ApiResponse<OnboardingStatus>> =
        await axios.get(`${BASE_URL}/onboarding/${pageKey}`, {
          headers: getHeaders(),
        });

      return response.data?.data || null;
    } catch (error: any) {
      // If 404, return null (no record exists)
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Save onboarding status for a page
   */
  saveStatus: async (
    pageKey: string,
    status: OnboardingStatusValue
  ): Promise<OnboardingStatus> => {
    try {
      const response: AxiosResponse<ApiResponse<OnboardingStatus>> =
        await axios.post(
          `${BASE_URL}/onboarding`,
          { pageKey, status },
          { headers: getHeaders() }
        );

      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Bulk save multiple onboarding status records
   */
  bulkSave: async (
    records: Array<{ pageKey: string; status: OnboardingStatusValue }>
  ): Promise<OnboardingStatus[]> => {
    try {
      const response: AxiosResponse<ApiResponse<OnboardingStatus[]>> =
        await axios.post(
          `${BASE_URL}/onboarding/bulk`,
          { records },
          { headers: getHeaders() }
        );

      return response.data?.data || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete onboarding status for a page (reset)
   */
  deleteStatus: async (pageKey: string): Promise<void> => {
    try {
      await axios.delete(`${BASE_URL}/onboarding/${pageKey}`, {
        headers: getHeaders(),
      });
    } catch (error) {
      throw error;
    }
  },
};

export default onboardingAPI;
